import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as fbSignOut,
  sendPasswordResetEmail,
  applyActionCode,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  type AuthError,
  type Auth,
  type UserCredential,
} from 'firebase/auth'
import { getFirebaseAuth } from './firebase'
import { AuthContext, type AuthApi } from './AuthContext'
import { authStore, type AuthState } from './authStore'
import { datadogRum } from './datadog'
import { resolveAppleIdentity, identityResolverBaseUrl } from './appleIdentityResolver'
import { beginAppleIdTokenCapture } from './appleIdTokenCapture'
import {
  requestRepairPlan,
  startRepairProbeAfterNotice,
  readRepairFragment,
  appleResolveOnSuccessEnabled,
} from './appleRepairFlow'
import {
  beginSsoLogin,
  endSsoSessionRow,
  markSsoSignedOut,
  navigateToSsoLogout,
  readAuthConfig,
} from './ssoLogin'

// Sign-in failures that mean "this Apple identity collided with an existing
// account" — the question the identity resolver exists to answer.
//
// WHICH code arrives depends on what the matched account already holds, and the
// first implementation of this branch got it backwards: it keyed on
// account-exists-with-different-credential, which is in fact the case we
// DECLINE, and missed provider-already-linked, which is the case we exist for.
const RESOLVABLE_SIGN_IN_CODES = new Set([
  // The matched account already holds an apple.com provider carrying a
  // different subject — the other developer team's. This is the two-teams case
  // and the reason this path exists at all. Firebase does not attach the
  // credential to this error, which is why the capture below is needed.
  'auth/provider-already-linked',
  // The matched account holds a different provider: a password or Google
  // signup owns the address. The resolver answers link_required and we rethrow,
  // but asking costs one request and beats assuming.
  'auth/account-exists-with-different-credential',
  // The incoming credential is already linked to some other account.
  'auth/credential-already-in-use',
])

// Runs the cross-team repair check after a successful Apple sign-in.
//
// Called only from the SUCCESS path of signInWithApple, never from its catch
// block -- which is why every failure in here is swallowed rather than
// rethrown. The user is already signed in; unlike the collision branch below
// (which rethrows, because a real sign-in error is what correctly describes
// that situation), there is no error here worth preserving. Nothing may turn a
// working sign-in into a broken one, including a bug in this function itself,
// hence the catch-all around the whole body.
//
// The decision is the server's, not ours: both halves of it -- whether an
// alias row already names a canonical account, and whether this sign-in is
// what created the account we landed on -- are only knowable server-side. The
// client asks and obeys. See appleRepairFlow.ts.
async function afterSuccessfulAppleSignIn(
  auth: Auth,
  result: UserCredential,
  recordOutcome: (outcome: string) => void,
  setCheckingExistingAccount: (checking: boolean) => void,
): Promise<void> {
  try {
    const baseUrl = identityResolverBaseUrl()
    const idToken = OAuthProvider.credentialFromResult(result)?.idToken
    if (!idToken || !baseUrl || !appleResolveOnSuccessEnabled()) return

    const plan = await requestRepairPlan(idToken, baseUrl)

    if (plan.kind === 'sign-in') {
      // An alias row names the canonical account. This is the steady state for
      // an already-repaired user: it runs on EVERY later sign-in, because the
      // shell account they land on is permanent -- it holds their local Apple
      // subject, and a Firebase user may carry only one apple.com provider, so
      // the shell can never be merged away.
      try {
        await signInWithCustomToken(auth, plan.customToken)
        recordOutcome('repair-resolved')
      } catch (customTokenErr) {
        // They are already signed in to the account Apple landed them on -- a
        // failed swap must not take that away.
        recordOutcome('repair-resolve-sign-in-failed')
        console.error('[AuthProvider] signInWithCustomToken failed after repair plan', customTokenErr)
      }
      return
    }

    if (plan.kind === 'probe') {
      // Leaves this page after a fixed, visible delay -- see
      // startRepairProbeAfterNotice and APPLE_REPAIR_NOTICE_DELAY_MS in
      // appleRepairFlow.ts for why the pause exists. The outcome arrives in
      // the fragment on the way back, consumed by AuthProvider's mount
      // effect. Declines when it cannot mint a binding to tie the outcome to
      // this tab (no sessionStorage, no crypto). Without one the returning
      // token could not be verified as ours, so not starting is the correct
      // answer.
      if (await startRepairProbeAfterNotice(idToken, baseUrl, setCheckingExistingAccount)) {
        recordOutcome('repair-probe-started')
      } else {
        recordOutcome('repair-probe-unbindable')
      }
      return
    }

    // 'none' is the common case and covers every failure inside
    // requestRepairPlan, which fails closed rather than throwing.
    recordOutcome('repair-none')
  } catch (unexpected) {
    recordOutcome('repair-check-failed')
    console.error('[AuthProvider] unexpected error after a successful Apple sign-in', unexpected)
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' })
  const auth = getFirebaseAuth()

  // True while the app-level "Checking for an existing account…" notice
  // should be showing -- from the moment a probe plan arrives (the user is
  // already `signed-in`, so SignInPage has already navigated away and cannot
  // host this itself) until either the probe declines to start or the
  // navigation to Apple's consent screen replaces this page.
  const [checkingExistingAccount, setCheckingExistingAccount] = useState(false)

  // Read once per mount. The runtime config is fixed for the page's lifetime,
  // and a mode that changed under a mounted provider would leave half the api
  // pointing at the other one.
  const [authConfig] = useState(() => readAuthConfig())

  useEffect(() => {
    return onAuthStateChanged(
      auth,
      (user) => {
        const next: AuthState = user ? { status: 'signed-in', user } : { status: 'signed-out' }
        setState(next)
        authStore.set(next)
        if (user) {
          datadogRum.setUser({ id: user.uid })
        } else {
          datadogRum.clearUser()
        }
      },
      (err) => {
        const next: AuthState = { status: 'error', detail: err.message }
        setState(next)
        authStore.set(next)
        datadogRum.clearUser()
      },
    )
  }, [])

  // Records that Apple cross-team identity resolution was attempted, and what
  // came of it — never the Apple ID token, the uid, or the email. The feature
  // is remote-configured, currently unreachable from any repository but this
  // one, and (once the resolver ships) depends on a service this repo does
  // not own; without this, there is no way to tell "nobody hit the collision
  // path" apart from "the CORS preflight blocked every call" apart from "the
  // resolver rejects everyone". Swallows any error of its own — a RUM call
  // that throws must never take down sign-in.
  const recordAppleResolutionOutcome = (outcome: string) => {
    try {
      datadogRum.addAction('apple_identity_resolution', { outcome })
    } catch {
      // Telemetry is best-effort; sign-in must proceed regardless.
    }
  }

  // Redeems a repair outcome the server put in the URL fragment.
  //
  // Its own effect, separate from the sign-in path, because the browser has
  // been away to Apple and back: this is a fresh page load, and the promise
  // chain that started the probe is long gone. It runs before any user
  // interaction, so a repaired user never sees the shell account's data.
  //
  // Deliberately NOT gated on appleResolveOnSuccessEnabled: a user could be
  // mid-flow when the flag is turned off, and stranding them holding a token
  // nothing will redeem is worse than honouring it. Redeeming adds no
  // capability either -- only our own server can mint a custom token, so
  // anyone able to supply one could already sign in with it.
  useEffect(() => {
    // The repair flow fixes an Apple sign-in performed on THIS origin. In sso
    // mode there is none, and the fragment it reads cannot appear.
    if (authConfig.mode === 'sso') return
    const outcome = readRepairFragment()
    if (!outcome) return
    if (outcome.kind !== 'token') {
      // 'no_other_account' is the ordinary miss and needs no user-facing
      // message: they stay on the account they signed in to, which is what
      // would have happened without the flow at all.
      recordAppleResolutionOutcome(`repair-returned-${outcome.code}`)
      return
    }
    void signInWithCustomToken(auth, outcome.customToken).then(
      () => recordAppleResolutionOutcome('repair-redeemed'),
      (err: unknown) => {
        // The token is single-use and now spent, so there is nothing to retry.
        // They remain signed in to the shell account, which is the state they
        // were in before the probe.
        recordAppleResolutionOutcome('repair-redeem-failed')
        console.error('[AuthProvider] signInWithCustomToken failed redeeming a repair token', err)
      },
    )
  }, [])

  const localApi = useMemo<AuthApi>(() => ({
    state,
    checkingExistingAccount,
    authMode: 'local',
    signIn: async (email, password) => { await signInWithEmailAndPassword(auth, email, password) },
    signInWithGoogle: async () => { await signInWithPopup(auth, new GoogleAuthProvider()) },
    signInWithApple: async () => {
      // Instruments fetch for this one attempt so a PROVIDER_ALREADY_LINKED
      // response's Apple ID token — which the SDK discards before any public
      // API can see it — is still reachable below. appleIdTokenCapture.ts
      // explains why that is necessary and why it cannot fail open.
      //
      // Only where resolution can actually happen. Three apps mount this
      // provider; wehunt-web and viltrapport-web configure a resolver, but
      // asf-fin-heatmap does not, and it would otherwise carry the
      // workaround's whole risk surface for an outcome that can only ever be
      // 'not-configured'.
      const capture = identityResolverBaseUrl()
        ? beginAppleIdTokenCapture()
        : { idToken: () => undefined, stop: () => {} }
      try {
        const result = await signInWithPopup(auth, new OAuthProvider('apple.com'))
        // .catch(() => {}) here, not just afterSuccessfulAppleSignIn's own
        // internal catch-all, so the "a probe failure cannot fail this
        // sign-in" guarantee holds STRUCTURALLY rather than by convention: if
        // this awaited inside the try below and someone later narrows that
        // catch (err) block or adds an early throw above it, a rejection
        // here would flow straight into it and rethrow, turning a working
        // sign-in into a broken one. Swallowing at the call site means that
        // can't happen regardless of what the catch block below does.
        await afterSuccessfulAppleSignIn(
          auth,
          result,
          recordAppleResolutionOutcome,
          setCheckingExistingAccount,
        ).catch(() => {})
      } catch (err) {
        // Sign in with Apple issues a subject identifier per Apple developer
        // TEAM, and our apps span two teams. A user whose Apple identity was
        // minted under the other team collides here on their email address:
        // Firebase will not create a duplicate, and cannot link the credential
        // either, because the account it collided with already holds its one
        // apple.com provider entry. The server owns the mapping from that
        // identifier to the account holding the user's history, and hands back
        // a custom token for it.
        //
        // Anything we cannot resolve rethrows the ORIGINAL error, so the
        // existing sign-in error copy still describes what the user hit.
        const code = (err as { code?: string }).code
        if (!code || !RESOLVABLE_SIGN_IN_CODES.has(code)) throw err
        const baseUrl = identityResolverBaseUrl()
        if (!baseUrl) {
          recordAppleResolutionOutcome('not-configured')
          throw err
        }

        // Two routes to the token, because Firebase tags only some of these
        // errors with the credential. Supported route first; the captured
        // response only when there is nothing else.
        const idToken =
          OAuthProvider.credentialFromError(err as AuthError)?.idToken ?? capture.idToken()
        if (!idToken) {
          // Neither tagged nor captured — which in practice means the response
          // shape the capture depends on has changed. A distinct outcome so
          // that arrives as a signal rather than as silence.
          recordAppleResolutionOutcome('no-id-token')
          throw err
        }

        const outcome = await resolveAppleIdentity(idToken, baseUrl)
        recordAppleResolutionOutcome(outcome.kind)
        if (outcome.kind !== 'resolved') throw err

        try {
          await signInWithCustomToken(auth, outcome.customToken)
        } catch (customTokenErr) {
          // The custom token can be stale, or its service account revoked,
          // between the resolver minting it and this call — a real if rare
          // race. Whatever went wrong here, the branch's whole point is that
          // the ORIGINAL Firebase error is what still describes the user's
          // situation correctly; a raw `auth/invalid-custom-token` would not.
          recordAppleResolutionOutcome('custom-token-sign-in-failed')
          console.error(
            '[AuthProvider] signInWithCustomToken failed after Apple identity resolution',
            customTokenErr,
          )
          throw err
        }
      } finally {
        capture.stop()
      }
    },
    signUp: async (email, password) => { await createUserWithEmailAndPassword(auth, email, password) },
    signOut: async () => { await fbSignOut(auth) },
    sendPasswordReset: async (email) => { await sendPasswordResetEmail(auth, email) },
    changePassword: async (currentPassword, newPassword) => {
      const user = auth.currentUser
      if (user === null || user.email === null) {
        // Either the session is gone or this account signs in through a
        // provider and has no email credential to re-verify. Both are answered
        // by signing in again, so they carry the code that says exactly that
        // rather than a bare throw the caller would have to render generically.
        throw Object.assign(new Error('No email-credential user is signed in.'), {
          code: 'auth/requires-recent-login',
        })
      }
      await reauthenticateWithCredential(
        user,
        EmailAuthProvider.credential(user.email, currentPassword),
      )
      await updatePassword(user, newPassword)
    },
    applyActionCode: async (oobCode) => { await applyActionCode(auth, oobCode) },
    setRememberMe: async (remember) => {
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence)
    },
  }), [state, checkingExistingAccount])

  /**
   * The api the app actually sees.
   *
   * sso mode is the local api MINUS the credential entry points, plus the two
   * things sso owns: starting a login, and ending the session on both sides.
   * Changing a password and applying an action code are account operations
   * rather than login mechanics, so they are shared verbatim.
   */
  const api = useMemo<AuthApi>(() => {
    if (authConfig.mode !== 'sso') return localApi
    return {
      state,
      checkingExistingAccount,
      authMode: 'sso',
      startSsoLogin: (returnTo) => beginSsoLogin(returnTo),
      signOut: async () => {
        // Mark the sign-out BEFORE anything else. The local sign-out below
        // flips the auth store, which sends the app to the sign-in page --
        // and in sso mode that page starts a fresh login, whose navigation
        // would race the one to sso's logout. The marker suppresses an
        // automatic start for a minute, which covers that hop, the tab sso
        // sends back to /sign-in afterwards, and every other tab of the app
        // that is still mounted while the logout is in flight.
        markSsoSignedOut()
        // Before the local sign-out, because the bearer sso needs to accept this
        // as proof of ownership is the credential about to be discarded.
        await endSsoSessionRow(auth.currentUser)
        // Local sign-out next: the hand-off navigation can be slow or
        // blocked, and a tab left signed in after the user asked to leave is
        // the worse of the two failures.
        await fbSignOut(auth)
        navigateToSsoLogout()
      },
      // Persistence in sso mode is local, matching sso's own session, so there
      // is no "remember me" choice left to honour.
      setRememberMe: async () => {},
      changePassword: localApi.changePassword,
      applyActionCode: localApi.applyActionCode,
    }
  }, [authConfig, localApi, state, checkingExistingAccount])

  return <AuthContext.Provider value={api}>{children}</AuthContext.Provider>
}
